import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { StepIndicator } from "@/components/layout/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import {
  Package,
  ListChecks,
  Heart,
  Droplets,
  Activity,
  Thermometer,
  Stethoscope,
  Eye,
  Gauge,
  Zap,
  ChevronRight,
  ArrowRight,
  User,
  Check,
  FileText,
  AlertCircle
} from "lucide-react";

// Icon mapping for services
const serviceIcons = {
  emocromo: Droplets,
  glicemia: Droplets,
  emoglobina_glicata: Droplets,
  colesterolo: Droplets,
  trigliceridi: Droplets,
  pressione: Gauge,
  ossimetria: Activity,
  ecg: Heart,
  spirometria: Activity,
  temperatura: Thermometer,
  peso: Gauge,
  visus: Eye
};

// Bundle icons and colors
const bundleStyles = {
  diabetes: { icon: Droplets, color: "text-info", bgColor: "bg-info/10" },
  cardiovascular: { icon: Heart, color: "text-destructive", bgColor: "bg-destructive/10" },
  respiratory: { icon: Activity, color: "text-primary", bgColor: "bg-primary/10" },
  complete: { icon: Stethoscope, color: "text-secondary", bgColor: "bg-secondary/10" },
  basic: { icon: Gauge, color: "text-success", bgColor: "bg-success/10" }
};

export const ServicesPage = () => {
  const navigate = useNavigate();
  const { selectedPatient, selectedServices, setSelectedServices } = useAppContext();
  const [activeTab, setActiveTab] = useState("bundles");
  const [selectedBundle, setSelectedBundle] = useState(null);

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
    setSelectedBundle(null); // Clear bundle selection when manually selecting
  };

  const selectBundle = (bundle) => {
    setSelectedBundle(bundle.id);
    setSelectedServices(bundle.services);
    toast.success(`Bundle selezionato: ${bundle.name}`, {
      description: `${bundle.services.length} prestazioni incluse`
    });
  };

  const handleContinue = () => {
    if (selectedServices.length === 0) {
      toast.error("Seleziona almeno una prestazione");
      return;
    }
    navigate("/devices");
  };

  const getSelectedServiceDetails = () => {
    return servicesData.filter(s => selectedServices.includes(s.id));
  };

  const getTotalDuration = () => {
    const details = getSelectedServiceDetails();
    const totalMinutes = details.reduce((acc, s) => acc + s.duration, 0);
    return totalMinutes;
  };

  // Check if any selected service requires report
  const hasServicesRequiringReport = () => {
    return getSelectedServiceDetails().some(s => s.requiresReport);
  };

  // Count services requiring report
  const countServicesRequiringReport = () => {
    return getSelectedServiceDetails().filter(s => s.requiresReport).length;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Selezione Prestazioni" />
      <StepIndicator currentStep={2} />
      
      <main className="flex-1 px-4 pb-6 md:px-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Patient info card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{selectedPatient?.name}</p>
                  <p className="text-sm text-muted-foreground font-mono">
                    {selectedPatient?.fiscalCode}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Page header */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Seleziona Prestazioni
            </h1>
            <p className="mt-1 text-muted-foreground">
              Scegli un bundle preconfigurato o seleziona singole prestazioni
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="bundles" className="gap-2 text-sm md:text-base">
                <Package className="h-4 w-4" />
                Bundle
              </TabsTrigger>
              <TabsTrigger value="single" className="gap-2 text-sm md:text-base">
                <ListChecks className="h-4 w-4" />
                Singole Prestazioni
              </TabsTrigger>
            </TabsList>

            {/* Bundles tab */}
            <TabsContent value="bundles" className="mt-6">
              <div className="grid gap-4 md:grid-cols-2">
                {serviceBundlesData.map((bundle, index) => {
                  const style = bundleStyles[bundle.id] || bundleStyles.basic;
                  const Icon = style.icon;
                  const isSelected = selectedBundle === bundle.id;
                  
                  return (
                    <Card
                      key={bundle.id}
                      className={cn(
                        "cursor-pointer transition-all animate-fade-in-up",
                        isSelected 
                          ? "border-primary ring-2 ring-primary/20" 
                          : "hover:border-primary/50"
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => selectBundle(bundle)}
                      data-testid={`bundle-${bundle.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className={cn("p-2 rounded-lg", style.bgColor)}>
                            <Icon className={cn("h-6 w-6", style.color)} />
                          </div>
                          <div className="flex items-center gap-2">
                            {bundle.requiresReport && (
                              <Badge variant="outline" className="gap-1 bg-warning/10 text-warning border-warning/30 text-xs">
                                <FileText className="h-3 w-3" />
                                Referto
                              </Badge>
                            )}
                            {isSelected && (
                              <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                                <Check className="h-4 w-4 text-primary-foreground" />
                              </div>
                            )}
                          </div>
                        </div>
                        <CardTitle className="text-lg mt-3">{bundle.name}</CardTitle>
                        <CardDescription>{bundle.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5">
                          {bundle.services.map(serviceId => {
                            const service = servicesData.find(s => s.id === serviceId);
                            return (
                              <Badge 
                                key={serviceId} 
                                variant="secondary" 
                                className={cn(
                                  "text-xs",
                                  service?.requiresReport && "border border-warning/30"
                                )}
                              >
                                {service?.name || serviceId}
                                {service?.requiresReport && (
                                  <FileText className="h-3 w-3 ml-1 text-warning" />
                                )}
                              </Badge>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Individual services tab */}
            <TabsContent value="single" className="mt-6">
              <ScrollArea className="h-[400px] md:h-[450px]">
                <div className="space-y-6 pr-4">
                  {/* Group by category */}
                  {["Analisi", "Parametri", "Diagnostica"].map(category => {
                    const categoryServices = servicesData.filter(s => s.category === category);
                    
                    return (
                      <div key={category}>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                          {category}
                        </h3>
                        <div className="space-y-2">
                          {categoryServices.map((service, index) => {
                            const Icon = serviceIcons[service.id] || Activity;
                            const isSelected = selectedServices.includes(service.id);
                            
                            return (
                              <Card
                                key={service.id}
                                className={cn(
                                  "cursor-pointer transition-all",
                                  isSelected 
                                    ? "border-primary bg-primary/5" 
                                    : "hover:border-primary/30",
                                  service.requiresReport && !isSelected && "border-warning/30"
                                )}
                                onClick={() => toggleService(service.id)}
                                data-testid={`service-${service.id}`}
                              >
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-4">
                                    <Checkbox
                                      checked={isSelected}
                                      className="h-5 w-5"
                                    />
                                    <div className={cn(
                                      "p-2 rounded-lg",
                                      service.requiresReport ? "bg-warning/10" : "bg-muted"
                                    )}>
                                      <Icon className={cn(
                                        "h-4 w-4",
                                        service.requiresReport ? "text-warning" : "text-muted-foreground"
                                      )} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-medium">{service.name}</h4>
                                        {service.requiresReport && (
                                          <Badge variant="outline" className="gap-1 bg-warning/10 text-warning border-warning/30 text-xs">
                                            <FileText className="h-3 w-3" />
                                            Referto
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground">
                                        {service.description}
                                      </p>
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      {service.duration} min
                                    </Badge>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Selection summary & continue */}
          <Card className="sticky bottom-4 shadow-lg border-2">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="default" className="text-sm">
                      {selectedServices.length} prestazioni
                    </Badge>
                    <Badge variant="outline" className="text-sm">
                      ~{getTotalDuration()} min
                    </Badge>
                    {hasServicesRequiringReport() && (
                      <Badge variant="outline" className="text-sm gap-1 bg-warning/10 text-warning border-warning/30">
                        <FileText className="h-3 w-3" />
                        {countServicesRequiringReport()} richiedono referto
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedServices.length === 0 
                      ? "Nessuna prestazione selezionata"
                      : getSelectedServiceDetails().map(s => s.name).join(", ")
                    }
                  </p>
                  {hasServicesRequiringReport() && (
                    <p className="text-xs text-warning mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Alcune prestazioni richiedono referto medico prima della condivisione
                    </p>
                  )}
                </div>
                <Button
                  variant="premium"
                  size="lg"
                  onClick={handleContinue}
                  disabled={selectedServices.length === 0}
                  className="gap-2"
                  data-testid="continue-btn"
                >
                  Continua
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ServicesPage;